import { Component, ErrorInfo, ReactNode } from 'react';
import { Container, Paper, Title, Text, Button, Stack } from '@mantine/core';

interface Props {
  children: ReactNode;
  /** When provided (e.g. from Sentry fallback), forces the error state. */
  error?: Error | unknown;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  render() {
    const displayError =
      this.state.hasError
        ? this.state.error
        : this.props.error instanceof Error
          ? this.props.error
          : this.props.error
            ? new Error(String(this.props.error))
            : null;

    if (displayError) {
      return (
        <Container size="sm" py="xl">
          <Paper shadow="md" p="xl" radius="md" withBorder role="alert" aria-live="assertive">
            <Stack align="center" gap="md">
              <Title order={2}>Something went wrong</Title>
              <Text c="dimmed" ta="center">
                An unexpected error occurred. Please try reloading the page.
              </Text>
              {import.meta.env.DEV && displayError && (
                <Text size="sm" c="red" style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                  {displayError.message}
                </Text>
              )}
              <Button onClick={() => window.location.reload()}>
                Reload Page
              </Button>
            </Stack>
          </Paper>
        </Container>
      );
    }

    return this.props.children;
  }
}
